/**
 * Mutation hook for 一覧印刷 on 訪問予定表(施術師毎) — opens PDF in a new tab.
 */

import { useMutation } from '@tanstack/react-query';
import useAppModal from '@/hooks/useAppModal';
import { useClinicContext } from '@/hooks/main/useClinicContext';
import { useTheme } from '@shared/providers/theme/ThemeContext';
import { SERVICE_TYPE_A2, SERVICE_TYPE_R2 } from '@/constants/serviceType';
import { printHomeVisitMassagers } from '@/services/main/homeVisitMassager';
import type { PrintHomeVisitMassagerPayload } from '@/pages/main/homeVisitMassager/types';

function resolveServiceType(theme: { serviceType?: string } | undefined): string {
  if (theme?.serviceType === SERVICE_TYPE_A2 || theme?.serviceType === SERVICE_TYPE_R2) {
    return theme.serviceType;
  }
  return typeof localStorage !== 'undefined'
    ? localStorage.getItem('service_type') || SERVICE_TYPE_A2
    : SERVICE_TYPE_A2;
}

interface BlobErrorResponse {
  response?: { data?: Blob };
}

// Backend returns a JSON error but response type is blob -> response is wrapped in a Blob,
// so parse text -> JSON to get the message.
async function readBlobErrorMessage(err: BlobErrorResponse): Promise<string | null> {
  const data = err.response?.data;
  if (!(data instanceof Blob)) return null;
  try {
    const json = JSON.parse(await data.text()) as { message?: string };
    return json.message ?? null;
  } catch {
    return null;
  }
}

export function usePrintHomeVisitMassagerMutation() {
  const { tenant, clinicNumber } = useClinicContext();
  const theme = useTheme();
  const modal = useAppModal();

  const { mutateAsync, isPending: isLoading } = useMutation<
    Blob,
    Error,
    PrintHomeVisitMassagerPayload
  >({
    mutationFn: payload =>
      printHomeVisitMassagers(tenant, clinicNumber, payload, resolveServiceType(theme)),
  });

  const triggerPrint = async (payload: PrintHomeVisitMassagerPayload) => {
    const tab = window.open('', '_blank');

    try {
      const blob = await mutateAsync(payload);
      const url = URL.createObjectURL(blob);

      if (tab) {
        tab.location.href = url;
      } else {
        window.open(url, '_blank');
      }

      setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } catch (err) {
      tab?.close();
      const message =
        (await readBlobErrorMessage(err as BlobErrorResponse)) ?? '印刷に失敗しました。';
      modal.error({ title: 'エラー', content: message, okText: 'OK' });
    }
  };

  return { triggerPrint, isLoading };
}
